/**
 * Brightcove video upload (init, complete, query).
 * Uses generated client: getImproveAPI().videoBrightcoveController* and gen types.
 */
import type {AxiosRequestConfig} from "axios";
import {
	getImproveAPI,
	type InitBrightcoveBody,
	type CompleteBrightcoveBody,
	type QueryBrightcoveBody
} from "../gen";
import {useQuery} from "@tanstack/react-query";
import {useImproveAxios} from "@/app2/shared/hooks";
export const BRIGHTCOVE_QUERY_KEYS = {
	VIDEO_QUERY: "brightcove-video-query",
	videoQuery: (videoId: string | null) =>
		[BRIGHTCOVE_QUERY_KEYS.VIDEO_QUERY, videoId] as const
} as const;

/** Response body from init (backend returns { success, data? } | { success: false, error }) */
export type BrightcoveInitResponse =
	| { success: true; data: BrightcoveInitData }
	| { success: false; error: string };

export type BrightcoveInitData = {
	videoId: string;
	fileName: string;
	credentials: {
		bucket: string;
		object_key: string;
		access_key_id: string;
		secret_access_key: string;
		session_token: string;
		video_id: string;
		api_request_url: string;
	};
	accountId?: string;
};

/** Response body from complete */
export type BrightcoveCompleteResponse =
	| { success: true; data?: unknown }
	| { success: false; error: string };

/** Response body from query */
export type BrightcoveQueryResponse =
	| { success: true; videoId: string; duration?: number | null; thumbnailUrl?: string | null }
	| { success: false; videoId?: string | null; error: string };

/**
 * Whether the Brightcove query indicates the asset is ready for playback.
 * The API may return `success: true` before transcoding finishes; only a positive
 * `duration` indicates processed metadata (and typically playable renditions).
 */
export function isBrightcoveQueryPlayable(
	data: BrightcoveQueryResponse | undefined
): boolean {
	if (data == null || data.success !== true) return false;
	return typeof data.duration === "number" && data.duration > 0;
}

export type {InitBrightcoveBody, CompleteBrightcoveBody, QueryBrightcoveBody};
/** @deprecated Use CompleteBrightcoveBody */
export type BrightcoveCompleteBody = CompleteBrightcoveBody;
/** @deprecated Use QueryBrightcoveBody */
export type BrightcoveQueryBody = QueryBrightcoveBody;
/** Config passed to brightcove init/complete/query (e.g. from useImproveAxios()) */
export type BrightcoveApiConfig = AxiosRequestConfig;

export async function brightcoveInit(
	config: Parameters<ReturnType<typeof getImproveAPI>["videoBrightcoveControllerInit"]>[1],
	body: InitBrightcoveBody
): Promise<BrightcoveInitResponse> {
	const {videoBrightcoveControllerInit} = getImproveAPI();
	const res = await videoBrightcoveControllerInit(body, config);
	return res.data as unknown as BrightcoveInitResponse;
}

export async function brightcoveComplete(
	config: Parameters<ReturnType<typeof getImproveAPI>["videoBrightcoveControllerComplete"]>[1],
	body: CompleteBrightcoveBody
): Promise<BrightcoveCompleteResponse> {
	const {videoBrightcoveControllerComplete} = getImproveAPI();
	const res = await videoBrightcoveControllerComplete(body, config);
	return res.data as unknown as BrightcoveCompleteResponse;
}

export async function brightcoveQuery(
	config: Parameters<ReturnType<typeof getImproveAPI>["videoBrightcoveControllerQuery"]>[1],
	body: QueryBrightcoveBody
): Promise<BrightcoveQueryResponse> {
	const {videoBrightcoveControllerQuery} = getImproveAPI();
	const res = await videoBrightcoveControllerQuery(body, config);
	return res.data as unknown as BrightcoveQueryResponse;
}

/**
 * Poll Brightcove video data (thumbnail, duration). Refetches every 1s when videoId is set.
 */
export function useBrightcoveQueryVideo(videoId: string | null) {
	const axiosConfig = useImproveAxios();
	const {videoBrightcoveControllerQuery} = getImproveAPI();
	return useQuery({
		queryKey: BRIGHTCOVE_QUERY_KEYS.videoQuery(videoId),
		queryFn: async () => {
			if (!videoId) throw new Error("videoId required");
			const res = await videoBrightcoveControllerQuery({videoId}, axiosConfig);
			const data = res.data as unknown as BrightcoveQueryResponse;
			if (data.success) return data;
			throw new Error((data as { error: string }).error);
		},
		enabled: !!videoId,
		refetchInterval: videoId ? 1000 : false,
		staleTime: 0
	});
}
